import { type ReactNode, useCallback } from "react";

import { cn } from "@btnet/utilities-core";

import { type ITrackingProp, track } from "utilities/analytics/analytics";

import {
    BTButtonDeprecated,
    type BTButtonDeprecatedType,
} from "commonComponents/btWrappers/BTButtonDeprecated/BTButtonDeprecated";
import { Hotkey } from "commonComponents/utilities/Hotkey/Hotkey";
import {
    type IHotkeyWithPopoverConfig,
    type THotkeyCommands,
} from "commonComponents/utilities/Hotkey/hotkey.types";
import { getHotkeyProps } from "commonComponents/utilities/Hotkey/hotkey.utility";

import "../BTLink/BTLink.less";

const newTabRel = "noopener noreferrer";

interface IExternalLinkButtonProps {
    "data-testid": string;
    /**
     * If provided with a button type, this will use button styling instead of standard link styling
     */
    buttonType: BTButtonDeprecatedType;
    isUnderline?: never;
}

interface IExternalLinkAnchorProps {
    "data-testid"?: string;
    isUnderline?: boolean;
    buttonType?: undefined;
}

interface IBTExternalLinkProps extends Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, "type"> {
    disabled?: boolean;
    /**
     * Reduce padding for use of buttons outside of button groups.
     * This is only applied if buttonType is defined, and is recommended
     * only to be used with buttontype="link"
     * @default false
     */
    isolated?: boolean;
    hotkey?: THotkeyCommands | IHotkeyWithPopoverConfig;
    icon?: ReactNode;
    children?: ReactNode;
}

function BTExternalLinkInternal({
    children,
    className = "",
    isUnderline = false,
    onClick,
    tracking,
    rel,
    "data-testid": dataTestId,
    buttonType,
    href,
    disabled,
    isolated,
    hotkey,
    icon,
    target = "_blank",
    ...otherProps
}: IBTExternalLinkProps & (IExternalLinkButtonProps | IExternalLinkAnchorProps) & ITrackingProp) {
    const handleClick = useCallback(
        (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
            if (onClick) {
                onClick(event);
            }
            tracking?.trackEvent({ event: "LinkClick" });
        },
        [onClick, tracking],
    );

    // noopener/noreferrer make the browser treat any named target like "_blank", opening a fresh
    // tab on every click instead of reusing the named window. Only harden "_blank" links (which
    // open a new tab to a separate destination); a named target is an intentional request to
    // reuse one tab, so leave its rel untouched.
    let modifiedRel = rel;
    if (target === "_blank") {
        modifiedRel = rel ? `${rel} ${newTabRel}` : newTabRel;
    }
    const linkClassNames = cn("BTLink", {
        [className]: className !== undefined,
        underline: isUnderline,
        disabled: disabled,
    });
    const component = buttonType ? (
        <BTButtonDeprecated
            data-testid={dataTestId}
            type={buttonType}
            href={href}
            target={target}
            rel={modifiedRel}
            disabled={disabled}
            className={className}
            isolated={isolated}
            onClick={handleClick}
            icon={icon}
        >
            {children}
        </BTButtonDeprecated>
    ) : (
        <a
            data-testid={dataTestId}
            className={linkClassNames}
            href={href}
            {...otherProps}
            target={target}
            rel={modifiedRel}
            onClick={handleClick}
        >
            {children}
        </a>
    );

    if (!hotkey) {
        return component;
    }

    return (
        <Hotkey
            {...getHotkeyProps(hotkey)}
            disabled={disabled}
            onCommand={() => {
                handleClick({} as React.MouseEvent<HTMLAnchorElement, MouseEvent>);
                if (href) {
                    window.open(href, target, modifiedRel);
                }
            }}
        >
            {component}
        </Hotkey>
    );
}

/**
 * Wrapper over anchor tag that opens in a new tab (target="_blank" by default) and adds
 * noopener/noreferrer to rel. Pass a named `target` to reuse a single tab across clicks
 * (noopener/noreferrer are intentionally omitted for named targets, since they would force
 * the browser to open a new tab each time).
 */
export const BTExternalLink = track((props) => ({
    element: "Link",
    uniqueId: props["data-testid"],
}))(BTExternalLinkInternal);
